import { useState } from 'react';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';

import { useUpdateChannelMutation } from '../slice/channelApi';

import type { EditChannelSchemaType } from './useChannelValidation';

const getAssignedPayload = (assignUsers?: any[]) => {
  const assignedUserIds: string[] = [];
  const assignedTeamIds: string[] = [];
  const assignedTypes: string[] = [];

  assignUsers?.forEach((u) => {
    if (u.assignedType === 'USER') assignedUserIds.push(u.id);
    if (u.assignedType === 'TEAM') assignedTeamIds.push(u.id);
  });

  if (assignedUserIds.length) assignedTypes.push('USER');
  if (assignedTeamIds.length) assignedTypes.push('TEAM');

  return {
    assignedType: assignedTypes,
    assignedUser: assignedUserIds,
    assignedTeam: assignedTeamIds,
  };
};

export const useChannelSubmit = (
  selectedChannel: any,
  channelState: any,
  FacebookDetail: any,
  instagramToken: any,
  messengerToken: any
) => {
  const { t } = useTranslate('translation');
  const [updateChannel, { isLoading: isUpdateChannelLoading }] = useUpdateChannelMutation();
  const [redirectToScriptPage, setRedirectToScriptPage] = useState(false);
  const [redirectToQrPage, setRedirectToQrPage] = useState(false);
  const [liveChatData, setLiveChatData] = useState<any>(null);

  const getUpdatedPayload = (data: Partial<EditChannelSchemaType>) => {
    const { channelType } = selectedChannel;
    const basePayload = {
      channelId: selectedChannel.id ?? '',
      channelName: data?.channelName ?? '',
    };

    const payloadBuilders: Record<string, () => any> = {
      WHATSAPP: () => ({
        ...basePayload,
        code: FacebookDetail?.main_token ?? '',
        ...(data.pin && /^\d{6}$/.test(data.pin) && { pin: data.pin }),
      }),
      EMAIL: () => {
        const provider =
          selectedChannel?.config?.settings?.provider ??
          selectedChannel?.EmailConfig?.[0]?.provider;

        if (provider === 'other') {
          const credentials = {
            email: data.email,
            imapPort: Number(data.imapPort),
            smtpPort: Number(data.smtpPort),
            imapLogin: data.imapLogin,
            smtpLogin: data.smtpLogin,
            imapAddress: data.imapAddress,
            smtpAddress: data.smtpAddress,
            imapPassword: data.imapPassword,
            smtpPassword: data.smtpPassword,
            imapEnableSsl: data.imapEnableSsl,
            smtpDomain: data.smtpDomain,
          };

          const settings = {
            email: data.email,
            provider: 'other',
            imapEnabled: data.imapEnabled,
            smtpEnabled: data.smtpEnabled,
          };

          return {
            channelId: selectedChannel.id ?? '',
            channelName: data.channelName,
            ...credentials,
            ...settings,
          };
        }

        const code =
          provider === 'google'
            ? channelState.googleEmailDetails?.code
            : channelState.microsoftEmailDetails?.code;

        return { ...basePayload, email: data.email, ...(code && { code }) };
      },
      LIVE_CHAT: () => ({
        ...basePayload,
        domainUrl: data.websiteDomin,
        welcomeTagline: data.welcomeTagline,
        welcomeTitle: data.welcomeTitle,
        widgetColor: data.widgetColor,
      }),
      INSTAGRAM: () => ({ ...basePayload, code: instagramToken }),
      MESSENGER: () => ({ ...basePayload, code: messengerToken }),
      SMS: () => ({
        ...basePayload,
        srcNumber: data.srcNumber,
        authToken: data.authToken,
        accountSid: data.accountSid,
        provider:
          selectedChannel?.config?.settings?.provider ??
          selectedChannel?.SMSConfig?.[0]?.provider ??
          '',
      }),
      LINE: () => ({
        ...basePayload,
        lineChannelId: data.lineChannelId,
        lineChannelSecret: data.lineChannelSecret,
      }),
      TELEGRAM: () => ({ ...basePayload, botToken: data.telegramBotToken }),
      // WHAT & WHY: Added payload builder for VOICE channels so that voice/DID numbers
      // and configuration updates are properly preserved when saving edits.
      VOICE: () => ({
        ...basePayload,
        didNum: data.srcNumber || selectedChannel?.config?.credentials?.didNum || '',
        didType: selectedChannel?.config?.credentials?.didType || '',
      }),
      // WHAT & WHY: Added payload builder for MICROSOFTTEAMS channels so that Microsoft Teams auth codes
      // and configuration updates are properly preserved when saving edits.
      MICROSOFTTEAMS: () => ({
        ...basePayload,
        ...(channelState?.microsoftTeamsDetails?.code && {
          code: channelState.microsoftTeamsDetails.code,
        }),
      }),
    };

    return payloadBuilders[channelType]?.() || basePayload;
  };

  const onSubmit = async (data: Partial<EditChannelSchemaType>) => {
    try {
      if (Object.keys(data).length > 0) {
        // WHAT & WHY: Strictly enforce selectedChannel.channelType in the payload
        // so that editing a channel cannot corrupt or overwrite the stored channelType in DB.
        const targetChannelType = selectedChannel?.channelType;
        if (!targetChannelType) {
          toast.error(t('CHANNEL_TYPE_INVALID'));
          return;
        }

        const payload = {
          ...getAssignedPayload(data.assignUsers),
          ...getUpdatedPayload(data),
          channelType: targetChannelType,
        };

        const response = await updateChannel({
          id: selectedChannel.id,
          updates: payload,
        }).unwrap();

        setLiveChatData(response?.data || null);
        toast.success(t('CHANNEL_UPDATED_SUCCESSFULLY'));

        if (selectedChannel.channelType === 'LIVE_CHAT') setRedirectToScriptPage(true);
        if (selectedChannel.channelType === 'LINE') setRedirectToQrPage(true);
      }
    } catch (err: any) {
      console.error('Error updating channel:', err);
      toast.error(t('CHANNEL_UPDATION_FAILED'));
    }
  };

  return {
    onSubmit,
    isUpdateChannelLoading,
    redirectToScriptPage,
    redirectToQrPage,
    liveChatData,
    setRedirectToScriptPage,
    setRedirectToQrPage,
  };
};
